import { fmtDateTime, todayIso, nowHM } from "@/lib/dateFormat";
import { UkDatePicker } from "@/components/UkDatePicker";
import { PaymentLog } from "@/components/PaymentLog";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Plus, Trash2, Download, FileText, Banknote, Send, Save, ClipboardCheck, CheckCheck, Package, AlertTriangle } from "lucide-react";
import { api, money, downloadPdf, API_ORIGIN } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Loading, ErrorState } from "@/components/States";
import { StatusChip } from "@/components/StatusChip";
import { NumberPlate } from "@/components/NumberPlate";
import { RelatedExpenses } from "@/components/RelatedExpenses";
import { PartPicker } from "@/components/PartPicker";
import { PurchaseModal } from "@/components/PurchaseModal";
import { DamagePhotos } from "@/components/DamagePhotos";
import { NextServiceCard } from "@/components/NextServiceCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { EmailComposeDialog } from "@/components/EmailComposeDialog";
import { JobWorkflow } from "@/components/JobWorkflow";
import { WhatsAppSendDialog, WhatsAppButton } from "@/components/WhatsAppSendDialog";
import { WhatsAppLog } from "@/components/WhatsAppLog";
import { waJobUpdateText } from "@/lib/whatsapp";
import { toast } from "sonner";

const STATUSES = ["open", "in_progress", "awaiting_parts", "completed", "invoiced"];

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [labour, setLabour] = useState([]);
  const [parts, setParts] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [emailCfg, setEmailCfg] = useState(null);
  const [waOpen, setWaOpen] = useState(false);
  const [waRefresh, setWaRefresh] = useState(0);
  const [apprOpen, setApprOpen] = useState(false);
  const [invOpen, setInvOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [team, setTeam] = useState([]);
  const [checkIns, setCheckIns] = useState([]);
  const [handovers, setHandovers] = useState([]);  const [approvals, setApprovals] = useState([]);
  const [jobPurchases, setJobPurchases] = useState([]);
  const [profit, setProfit] = useState(null);
  const [payments, setPayments] = useState(null);
  const [wfKey, setWfKey] = useState(0);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const j = await api(`/jobs/${id}`);
      setJob(j); setLabour(j.labour || []); setParts(j.parts || []); setDirty(false);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => { api("/team").then(setTeam).catch(() => {}); }, []);
  useEffect(() => { api(`/workshop/jobs/${id}/check-ins`).then((d) => setCheckIns(Array.isArray(d) ? d : [])).catch(() => {}); }, [id]);
  useEffect(() => { api(`/workshop/jobs/${id}/handovers`).then((d) => setHandovers(Array.isArray(d) ? d : [])).catch(() => {}); }, [id, job?.collected_at]);
  useEffect(() => { api("/approvals").then((d) => setApprovals(Array.isArray(d) ? d.filter((a) => a.job_id === id) : [])).catch(() => {}); }, [id, job?.updated_at]);
  const loadPurchases = () => api(`/purchases?job_id=${id}`).then((d) => setJobPurchases(Array.isArray(d) ? d : [])).catch(() => {});
  useEffect(() => { loadPurchases(); }, [id]);
  const loadProfit = () => api(`/jobs/${id}/profitability`).then(setProfit).catch(() => {});
  useEffect(() => { loadProfit(); }, [id, job?.updated_at]);
  const loadPayments = () => { setPayments(null); api(`/payments?job_id=${id}`).then(setPayments).catch(() => setPayments([])); };
  useEffect(() => { loadPayments(); }, [id, wfKey]);

  const patch = async (body, msg) => {
    try { const j = await api(`/jobs/${id}`, { method: "PATCH", body: JSON.stringify(body) }); setJob(j); if (msg) toast.success(msg); return j; }
    catch (e) { toast.error(e.message); throw e; }
  };
  const changeStatus = (s) => patch({ status: s }, "Status updated");
  const assign = (val) => patch({ assigned_to: val || null }, val ? "Job assigned" : "Job unassigned");
  const saveItems = async () => {
    setSaving(true);
    const coerce = (arr) => (arr || []).map((i) => ({ ...i, qty: Number(i.qty) || 0, unit_price: Number(i.unit_price) || 0 }));
    try { const j = await patch({ labour: coerce(labour), parts: coerce(parts) }, "Job saved"); setLabour(j.labour || []); setParts(j.parts || []); setDirty(false); }
    finally { setSaving(false); }
  };
  const pdf = async () => { try { await downloadPdf(`/jobs/${id}/work-order.pdf`, `work-order-${id}.pdf`); } catch (e) { toast.error(e.message); } };
  const downloadHandoverPdf = async () => { try { await downloadPdf(`/workshop/jobs/${id}/handover.pdf`, `handover-${id}.pdf`); } catch (e) { toast.error(e.message); } };
  const emailWorkOrder = () => setEmailCfg({
    endpoint: `/jobs/${id}/send`,
    title: "Email work order",
    defaultTo: job.customer?.email || "",
    defaultSubject: `Work order${job.title ? " — " + job.title : ""}`,
    attachmentLabel: "The work order",
    submitLabel: "SEND WORK ORDER",
  });
  const remove = async () => { if (!window.confirm("Delete this job?")) return; try { await api(`/jobs/${id}`, { method: "DELETE" }); toast.success("Job deleted"); navigate("/jobs"); } catch (e) { toast.error(e.message); } };

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const t = job.totals || {};

  const pendingAppr = approvals.find((a) => a.status === "pending");

  return (
    <div>
      <Link to="/jobs" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-2"><ArrowLeft className="h-4 w-4" />Jobs</Link>
      <PageHeader title={job.title} subtitle={job.customer?.name}
        actions={<>
          <button onClick={pdf} data-testid="job-pdf-btn" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5 text-sm font-bold text-midnight hover:bg-secondary"><Download className="h-4 w-4" />PDF</button>
          <button onClick={emailWorkOrder} data-testid="job-email-wo-btn" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5 text-sm font-bold text-midnight hover:bg-secondary"><Send className="h-4 w-4" />Email</button>
          <WhatsAppButton onClick={() => setWaOpen(true)} testId="job-whatsapp-btn" />
          <button onClick={remove} data-testid="job-delete-btn" className="rounded-lg border border-danger/30 bg-danger-bg px-3 py-2.5 text-sm font-bold text-danger"><Trash2 className="h-4 w-4" /></button>
        </>} />

      <JobWorkflow
        jobId={id} status={job.status} onRefresh={load} refreshKey={wfKey}
        actions={{
          checkIn: () => navigate(`/jobs/check-in?job_id=${id}`),
          startWork: () => changeStatus("in_progress"),
          markCompleted: () => changeStatus("completed"),
          openInvoice: () => setInvOpen(true),
          openPayment: () => setPayOpen(true),
          gotoHandover: () => navigate(`/jobs/handover?job_id=${id}`),
        }}
      />

      {pendingAppr && (
        <div className="mb-4 rounded-xl border border-warning/40 bg-warning-bg p-4" data-testid="awaiting-additional-approval">
          <div className="flex items-center gap-2 text-warning">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-[11px] font-bold uppercase tracking-widest">Awaiting Additional Approval</span>
          </div>
          <p className="mt-1 text-sm font-bold text-midnight">{pendingAppr.title}</p>
          <div className="mt-2 space-y-1">
            {(pendingAppr.items || []).map((it, i) => (
              <div key={i} className="flex justify-between text-xs text-slate-700" data-testid={`awaiting-item-${i}`}>
                <span>{it.description} {it.qty > 1 ? `× ${it.qty}` : ""}</span>
                <span className="font-semibold">{money((Number(it.qty) || 1) * (Number(it.unit_price) || 0))}</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">These items are not added to the job totals until the customer approves.</p>
        </div>
      )}

      {approvals.filter((a) => a.status === "approved" || a.status === "declined").length > 0 && (
        <div className="mb-4 rounded-xl border border-border bg-card p-4" data-testid="approval-history">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Customer Approval History</p>
          <div className="space-y-2">
            {approvals.filter((a) => a.status === "approved" || a.status === "declined").map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`approval-decision-${a.id}`}>
                <span className="min-w-0 truncate text-midnight"><b>{a.title}</b>{a.decided_at ? <span className="text-muted-foreground"> · {fmtDateTime(a.decided_at)}</span> : null}</span>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${a.status === "approved" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{a.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}


      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {/* Vehicle + status */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              {job.vehicle?.vrm && <NumberPlate vrm={job.vehicle.vrm} />}
              <div>
                <Link to={`/vehicles/${job.vehicle_id}`} data-testid="job-vehicle-link" className="font-bold text-midnight hover:text-brand-2">{job.vehicle?.make} {job.vehicle?.model}</Link>
                <p className="text-xs text-muted-foreground">{[job.vehicle?.year, job.vehicle?.colour].filter(Boolean).join(" · ")}{job.customer_id ? <> · <Link to={`/customers/${job.customer_id}`} data-testid="job-customer-link" className="font-semibold text-brand-2">{job.customer?.name || "Customer"}</Link></> : null}</p>
              </div>
            </div>
            <select value={job.status} onChange={(e) => changeStatus(e.target.value)} data-testid="job-status-select"
              className="rounded-lg border border-input bg-card px-3 py-2 text-sm font-bold outline-none focus:border-brand">
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ").toUpperCase()}</option>)}
            </select>
          </div>

          {job.owner_mismatch && (
            <div data-testid="owner-mismatch-warning" className="flex items-start gap-2 rounded-xl border border-warning/40 bg-warning-bg p-3 text-sm text-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>The customer on this job is <b>not</b> the registered owner of this vehicle. Check the records before invoicing.</span>
            </div>
          )}

          <LineItems title="Labour" items={labour} setItems={(v) => { setLabour(v); setDirty(true); }} kind="labour" />
          <PartsSection items={parts} setItems={(v) => { setParts(v); setDirty(true); }} />

          <DamagePhotos jobId={id} />

          <div className="rounded-xl border border-border bg-card p-4" data-testid="workshop-card">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold uppercase text-midnight">Check-In / Handover</h3>
              <div className="flex gap-2">
                <button onClick={() => navigate(`/jobs/check-in?job_id=${id}`)} data-testid="job-checkin-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-brand bg-brand-tertiary px-3 py-2 text-xs font-bold uppercase text-brand-2 hover:bg-brand-tertiary/70"><ClipboardCheck className="h-4 w-4" />Check In</button>
                {["completed", "invoiced"].includes(job.status) && (
                  <button onClick={() => navigate(`/jobs/handover?job_id=${id}`)} data-testid="job-handover-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-success/40 bg-success/10 px-3 py-2 text-xs font-bold uppercase text-success hover:bg-success/20"><CheckCheck className="h-4 w-4" />Handover</button>
                )}
              </div>
            </div>
            {checkIns.length === 0 ? (
              <p className="py-2 text-center text-xs text-muted-foreground">No check-in recorded yet. Record the vehicle&apos;s arrival condition and photos.</p>
            ) : (
              <div className="space-y-3">
                {checkIns.map((ci) => (
                  <div key={ci.id} className="rounded-lg border border-border bg-secondary/40 p-3" data-testid={`checkin-record-${ci.id}`}>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                      <span className="font-bold text-midnight">{fmtDateTime(ci.created_at)}</span>
                      {ci.mileage != null && <span className="text-muted-foreground">Mileage: <b className="text-midnight">{ci.mileage}</b></span>}
                      {ci.fuel_level && <span className="text-muted-foreground">Fuel: <b className="text-midnight">{ci.fuel_level}</b></span>}
                      {ci.checked_in_by_name && <span className="text-muted-foreground">By: <b className="text-midnight">{ci.checked_in_by_name}</b></span>}
                    </div>
                    {ci.existing_damage && <p className="mt-1 text-xs text-muted-foreground">Damage: {ci.existing_damage}</p>}
                    {ci.notes && <p className="mt-1 text-xs text-muted-foreground">Notes: {ci.notes}</p>}
                    {(ci.photos || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {ci.photos.map((p) => (
                          <a key={p.id} href={`${API_ORIGIN}${p.url}`} target="_blank" rel="noreferrer" data-testid={`checkin-photo-${p.id}`} className="h-16 w-16 overflow-hidden rounded-lg border border-border">
                            <img src={`${API_ORIGIN}${p.url}`} alt="" className="h-full w-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {handovers.length > 0 && (
              <div className="mt-3 space-y-3 border-t border-border pt-3">
                <p className="text-[11px] font-bold uppercase tracking-widest text-success">Handover / collection</p>
                {handovers.map((h) => (
                  <div key={h.id} className="rounded-lg border border-success/40 bg-success/10 p-3" data-testid={`handover-record-${h.id}`}>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                      <span className="inline-flex items-center gap-1 font-bold text-midnight"><CheckCheck className="h-3.5 w-3.5 text-success" />{fmtDateTime(h.created_at)}</span>
                      {h.mileage_out != null && <span className="text-muted-foreground">Mileage out: <b className="text-midnight">{h.mileage_out}</b></span>}
                      <span className="text-muted-foreground">Keys: <b className="text-midnight">{h.keys_returned ? "Returned" : "Not returned"}</b></span>
                      {h.handled_by_name && <span className="text-muted-foreground">By: <b className="text-midnight">{h.handled_by_name}</b></span>}
                    </div>
                    {h.final_condition && <p className="mt-1 text-xs text-muted-foreground">Condition: {h.final_condition}</p>}
                    {h.notes && <p className="mt-1 text-xs text-muted-foreground">Notes: {h.notes}</p>}
                    {(h.photos || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {h.photos.map((p) => (
                          <a key={p.id} href={`${API_ORIGIN}${p.url}`} target="_blank" rel="noreferrer" data-testid={`handover-photo-${p.id}`} className="h-16 w-16 overflow-hidden rounded-lg border border-border">
                            <img src={`${API_ORIGIN}${p.url}`} alt="" className="h-full w-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                    <button onClick={() => downloadHandoverPdf()} data-testid="handover-pdf-btn" className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-brand-2 hover:underline"><Download className="h-3.5 w-3.5" />Handover PDF</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <NextServiceCard
            vehicleId={job.vehicle_id}
            baselineMileage={job.mileage_out ?? job.mileage_in ?? (checkIns.find((c) => c.mileage != null)?.mileage ?? null)}
          />

          {dirty && (
            <button onClick={saveItems} disabled={saving} data-testid="job-save-items-btn" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3 text-sm font-extrabold tracking-wide text-white disabled:opacity-60">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="h-4 w-4" />SAVE CHANGES</>}
            </button>
          )}
        </div>

        {/* Totals + actions */}
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-5" data-testid="assign-card">
            <h3 className="mb-3 font-display text-lg font-bold uppercase text-midnight">Assigned To</h3>
            <select value={job.assigned_to || ""} onChange={(e) => assign(e.target.value)} data-testid="job-assign-select"
              className="w-full rounded-lg border border-input bg-card px-3 py-2.5 text-sm font-bold outline-none focus:border-brand">
              <option value="">Unassigned</option>
              {team.map((m) => <option key={m.id} value={m.id}>{m.name || m.email}{m.role === "owner" ? " (Owner)" : ""}</option>)}
            </select>
            {job.assignee && <p className="mt-2 text-xs text-muted-foreground" data-testid="job-assignee-name">Currently: <span className="font-bold text-midnight">{job.assignee.name || job.assignee.email}</span></p>}
          </div>

          <div className="rounded-xl border border-border bg-card p-5">
            <h3 className="mb-3 font-display text-lg font-bold uppercase text-midnight">Totals</h3>
            <Row label="Net" value={money(t.total_net)} />
            <Row label={`VAT (${t.vat_rate || 20}%)`} value={money(t.total_vat)} />
            <div className="my-2 border-t border-border" />
            <Row label="Total (inc. VAT)" value={money(t.total_gross)} big />
            <Row label="Paid" value={money(t.total_paid)} tone="success" />
            <Row label="Balance" value={money(t.balance)} tone={t.balance > 0 ? "warning" : "success"} />
            <div className="mt-2"><StatusChip status={t.payment_status} /></div>
            <div className="mt-3 border-t border-border pt-3"><PaymentLog logs={payments} /></div>
          </div>

          {profit && (
            <div className="rounded-xl border border-border bg-card p-5" data-testid="job-profitability">
              <h3 className="mb-3 flex items-center justify-between font-display text-lg font-bold uppercase text-midnight">
                Profitability
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${profit.is_final ? "bg-success/10 text-success" : "bg-brand-tertiary text-brand-2"}`}>{profit.is_final ? "FINAL" : "ESTIMATED"}</span>
              </h3>
              <Row label="Revenue (est.)" value={money(profit.estimated_revenue)} />
              {profit.has_invoices && <Row label="Revenue (invoiced)" value={money(profit.invoiced_revenue)} />}
              <Row label="Parts cost" value={money(profit.parts_cost)} />
              <div className="my-2 border-t border-border" />
              <Row label={profit.is_final ? "Profit (final)" : "Profit (est.)"} value={money(profit.is_final ? profit.final_profit : profit.estimated_profit)} big tone={(profit.is_final ? profit.final_profit : profit.estimated_profit) >= 0 ? "success" : "warning"} />
              <p className="mt-1 text-right text-xs text-muted-foreground" data-testid="job-margin">{profit.is_final ? profit.final_margin : profit.estimated_margin}% margin</p>
            </div>
          )}

          <div className="space-y-2">
            <button onClick={() => setPayOpen(true)} data-testid="add-payment-btn" className="flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-card py-3 text-sm font-bold text-midnight hover:bg-secondary"><Banknote className="h-4 w-4" />Record Payment</button>
            <button onClick={() => setApprOpen(true)} data-testid="request-approval-btn" className="flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-card py-3 text-sm font-bold text-midnight hover:bg-secondary"><Send className="h-4 w-4" />Request Approval</button>
            <button onClick={() => setOrderOpen(true)} data-testid="order-from-supplier-btn" className="flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-card py-3 text-sm font-bold text-midnight hover:bg-secondary"><Package className="h-4 w-4" />Order Part from Supplier</button>
            {job.status !== "invoiced" ? (
              <button onClick={() => setInvOpen(true)} data-testid="convert-invoice-btn" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3.5 text-sm font-extrabold tracking-wide text-white"><FileText className="h-4 w-4" />CREATE INVOICE</button>
            ) : (
              <>
                {job.invoice_number && <p className="rounded-lg bg-brand-tertiary p-3 text-center text-sm font-bold text-brand-2">Invoiced as {job.invoice_number}</p>}
                <button onClick={() => setInvOpen(true)} data-testid="supplementary-invoice-btn" className="flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-card py-3 text-sm font-bold text-midnight hover:bg-secondary"><FileText className="h-4 w-4" />Supplementary invoice</button>
              </>
            )}
          </div>
        </div>
      </div>

      <RelatedExpenses jobId={id} title="Linked expenses (job costs)" />
      {jobPurchases.length > 0 && (
        <div className="mt-6 rounded-xl border border-border bg-card p-5" data-testid="job-purchases">
          <h3 className="mb-3 flex items-center gap-2 font-display text-base font-bold uppercase text-midnight"><Package className="h-5 w-5 text-brand" />Supplier purchases for this job</h3>
          <div className="divide-y divide-border">
            {jobPurchases.map((p) => (
              <div key={p.id} data-testid={`job-purchase-${p.id}`} className="flex items-center gap-3 py-2.5 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-midnight">{p.supplier_name || "Supplier"}{p.supplier_invoice_number ? ` · ${p.supplier_invoice_number}` : ""}</p>
                  <p className="truncate text-xs text-muted-foreground">{(p.lines || []).filter((l) => l.job_id === id).map((l) => `${l.qty}× ${l.description}`).join(", ")}</p>
                </div>
                <span className="text-[11px] uppercase text-muted-foreground">{p.status} · {p.payment_status}</span>
                <span className="font-display font-bold text-midnight">{money(p.total)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <PaymentDialog open={payOpen} onClose={() => setPayOpen(false)} jobId={id} balance={t.balance} onDone={() => { load(); setWfKey((k) => k + 1); }} />
      <InvoiceDraftDialog open={invOpen} onClose={() => setInvOpen(false)} jobId={id} onCreated={(inv) => navigate(`/invoice/${inv.id}`)} />
      <PurchaseModal open={orderOpen} onClose={() => setOrderOpen(false)} presetJob={{ job_id: id, customer_id: job.customer_id, vehicle_id: job.vehicle_id }} onSaved={() => { load(); loadPurchases(); }} />
      <ApprovalDialog open={apprOpen} onClose={() => setApprOpen(false)} jobId={id} onDone={load} />
      <EmailComposeDialog cfg={emailCfg} onClose={() => setEmailCfg(null)} onSent={load} />
      <WhatsAppLog entityType="job" entityId={id} refreshKey={waRefresh} />
      <WhatsAppSendDialog
        open={waOpen} onClose={() => setWaOpen(false)} onSent={() => setWaRefresh((x) => x + 1)}
        customerId={job.customer_id} customerName={job.customer?.name} to={job.customer?.phone}
        defaultText={waJobUpdateText({ customerName: job.customer?.name, title: job.title, status: job.status })}
        kind="job_update" entityType="job" entityId={id}
      />
    </div>
  );
}

function Row({ label, value, big, tone }) {
  const tones = { success: "text-success", warning: "text-warning", "": "text-midnight" };
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`font-bold ${big ? "font-display text-2xl" : "text-sm"} ${tones[tone || ""]}`}>{value}</span>
    </div>
  );
}

function LineItems({ title, items, setItems, kind }) {
  const add = () => setItems([...items, { description: "", qty: 1, unit_price: "", kind }]);
  const update = (i, k, v) => { const c = [...items]; c[i] = { ...c[i], [k]: v }; setItems(c); };
  const remove = (i) => setItems(items.filter((_, j) => j !== i));
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-display text-lg font-bold uppercase text-midnight">{title}</h3>
        <button onClick={add} data-testid={`add-${kind}-btn`} className="inline-flex items-center gap-1 text-sm font-bold text-brand-2"><Plus className="h-4 w-4" />Add</button>
      </div>
      {items.length === 0 ? <p className="py-3 text-center text-xs text-muted-foreground">No {title.toLowerCase()} added.</p> : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`${kind}-item-${i}`}>
              <input value={it.description} onChange={(e) => update(i, "description", e.target.value)} placeholder="Description" data-testid={`${kind}-desc-${i}`}
                className="min-w-0 flex-1 rounded-md border border-input px-2.5 py-2 text-sm outline-none focus:border-brand" />
              <input type="number" step="0.01" value={it.qty} onChange={(e) => update(i, "qty", e.target.value)} data-testid={`${kind}-qty-${i}`} className="w-16 rounded-md border border-input px-2 py-2 text-center text-sm outline-none focus:border-brand" />
              <input type="number" step="0.01" value={it.unit_price} onChange={(e) => update(i, "unit_price", e.target.value)} data-testid={`${kind}-price-${i}`} className="w-24 rounded-md border border-input px-2 py-2 text-right text-sm outline-none focus:border-brand" />
              <button onClick={() => remove(i)} data-testid={`${kind}-remove-${i}`} className="text-danger"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PartsSection({ items, setItems }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const addManual = () => setItems([...items, { description: "", qty: 1, unit_price: "", kind: "part" }]);
  const addFromStock = (p) => setItems([...items, {
    description: p.name, qty: 1, unit_price: Number(p.unit_price) || 0,
    cost_price: p.cost_price != null ? Number(p.cost_price) : null,
    part_id: p.id, sku: p.sku, stock_qty: p.stock_qty, kind: "part",
  }]);
  const update = (i, k, v) => { const c = [...items]; c[i] = { ...c[i], [k]: v }; setItems(c); };
  const remove = (i) => setItems(items.filter((_, j) => j !== i));
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-display text-lg font-bold uppercase text-midnight">Parts</h3>
        <div className="flex items-center gap-3">
          <button onClick={() => setPickerOpen(true)} data-testid="add-part-from-stock-btn" className="inline-flex items-center gap-1 text-sm font-bold text-brand-2"><Package className="h-4 w-4" />From stock</button>
          <button onClick={addManual} data-testid="add-part-btn" className="inline-flex items-center gap-1 text-sm font-bold text-brand-2"><Plus className="h-4 w-4" />Manual</button>
        </div>
      </div>
      {items.length === 0 ? <p className="py-3 text-center text-xs text-muted-foreground">No parts added.</p> : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} data-testid={`part-item-${i}`}>
              <div className="flex items-center gap-2">
                <input value={it.description} onChange={(e) => update(i, "description", e.target.value)} placeholder="Description" data-testid={`part-desc-${i}`} readOnly={!!it.part_id}
                  className={`min-w-0 flex-1 rounded-md border border-input px-2.5 py-2 text-sm outline-none focus:border-brand ${it.part_id ? "bg-secondary font-semibold" : ""}`} />
                <input type="number" step="0.01" value={it.qty} onChange={(e) => update(i, "qty", e.target.value)} data-testid={`part-qty-${i}`} className="w-16 rounded-md border border-input px-2 py-2 text-center text-sm outline-none focus:border-brand" />
                <input type="number" step="0.01" value={it.unit_price} onChange={(e) => update(i, "unit_price", e.target.value)} data-testid={`part-price-${i}`} className="w-24 rounded-md border border-input px-2 py-2 text-right text-sm outline-none focus:border-brand" />
                <button onClick={() => remove(i)} data-testid={`part-remove-${i}`} className="text-danger"><Trash2 className="h-4 w-4" /></button>
              </div>
              {it.part_id && (
                <p className="mt-0.5 flex items-center gap-2 pl-1 text-[11px] text-muted-foreground" data-testid={`part-stock-info-${i}`}>
                  <Package className="h-3 w-3 text-brand-2" />From stock{it.sku ? ` · SKU ${it.sku}` : ""}
                  {it.stock_qty != null && (
                    <span className={Number(it.stock_qty) < 0 ? "font-semibold text-danger" : Number(it.stock_qty) <= 5 ? "font-semibold text-warning" : ""}>
                      {Number(it.stock_qty) < 0 && <AlertTriangle className="mr-0.5 inline h-3 w-3" />}
                      {it.stock_qty} in stock
                    </span>
                  )}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
      <PartPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={addFromStock} />
    </div>
  );
}

function InvoiceDraftDialog({ open, onClose, jobId, onCreated }) {
  const [bill, setBill] = useState(null);
  const [sel, setSel] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setBill(null);
    api(`/jobs/${jobId}/billable`).then((b) => {
      setBill(b);
      const s = {};
      (b.lines || []).forEach((l) => { s[l.line_id] = { include: l.qty_remaining > 0.0001, qty: l.qty_remaining }; });
      setSel(s);
    }).catch((e) => toast.error(e.message));
  }, [open, jobId]);

  const lines = (bill?.lines || []);
  const billable = lines.filter((l) => l.qty_remaining > 0.0001);
  const total = Object.entries(sel).reduce((acc, [lid, v]) => {
    const l = lines.find((x) => x.line_id === lid);
    return acc + (v.include ? (Number(v.qty) || 0) * (l?.unit_price || 0) : 0);
  }, 0);
  const setQty = (lid, qty) => setSel((s) => ({ ...s, [lid]: { ...s[lid], qty } }));
  const toggle = (lid) => setSel((s) => ({ ...s, [lid]: { ...s[lid], include: !s[lid]?.include } }));

  const create = async () => {
    const selections = Object.entries(sel)
      .filter(([, v]) => v.include && Number(v.qty) > 0)
      .map(([line_id, v]) => ({ line_id, qty: Number(v.qty) }));
    if (selections.length === 0) { toast.error("Select at least one item to invoice"); return; }
    setSaving(true);
    try {
      const inv = await api("/invoices", { method: "POST", body: JSON.stringify({ job_id: jobId, due_days: 14, line_selections: selections }) });
      toast.success(`Invoice ${inv.invoice_number} created`);
      onClose(); onCreated(inv);
    } catch (e) { toast.error(e.body?.detail?.message || e.message); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display uppercase">Create invoice from job</DialogTitle></DialogHeader>
        {!bill ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div>
        ) : billable.length === 0 ? (
          <div className="py-6 text-center" data-testid="invoice-draft-empty">
            <p className="text-sm font-semibold text-midnight">Everything on this job is already invoiced.</p>
            <p className="mt-1 text-xs text-muted-foreground">Add more labour or parts to the job to raise a supplementary invoice.</p>
          </div>
        ) : (
          <>
            <p className="mb-1 text-xs text-muted-foreground">Review the quantities to bill. Reduce a quantity to invoice partially — the rest stays available for a later invoice.</p>
            <div className="max-h-72 space-y-2 overflow-y-auto">
              {billable.map((l) => (
                <div key={l.line_id} data-testid={`invoice-draft-line-${l.line_id}`} className={`flex items-center gap-2 rounded-lg border p-2.5 ${sel[l.line_id]?.include ? "border-brand/40 bg-brand-tertiary/40" : "border-border"}`}>
                  <input type="checkbox" checked={!!sel[l.line_id]?.include} onChange={() => toggle(l.line_id)} data-testid={`invoice-draft-toggle-${l.line_id}`} className="h-4 w-4 accent-brand" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-midnight">{l.description || "(no description)"}</p>
                    <p className="text-[11px] uppercase text-muted-foreground">{l.kind} · {money(l.unit_price)} each · {l.qty_remaining} of {l.qty_total} left</p>
                  </div>
                  <input type="number" step="0.01" min="0" max={l.qty_remaining} value={sel[l.line_id]?.qty ?? ""} onChange={(e) => setQty(l.line_id, e.target.value)} disabled={!sel[l.line_id]?.include} data-testid={`invoice-draft-qty-${l.line_id}`}
                    className="w-16 rounded-md border border-input px-2 py-1.5 text-center text-sm outline-none focus:border-brand disabled:opacity-50" />
                </div>
              ))}
            </div>
            <div className="mt-2 flex items-center justify-between border-t border-border pt-3">
              <span className="text-sm text-muted-foreground">Net to invoice</span>
              <span className="font-display text-xl font-bold text-midnight" data-testid="invoice-draft-total">{money(total)}</span>
            </div>
          </>
        )}
        {bill && billable.length > 0 && (
          <DialogFooter>
            <button onClick={create} disabled={saving} data-testid="invoice-draft-create" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3 text-sm font-extrabold text-white disabled:opacity-60">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><FileText className="h-4 w-4" />CREATE INVOICE</>}
            </button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PaymentDialog({ open, onClose, jobId, balance, onDone }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [payDate, setPayDate] = useState(todayIso());
  const [payTime, setPayTime] = useState(nowHM());
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState(null);
  useEffect(() => {
    if (open) {
      setAmount(balance > 0 ? String(balance) : ""); setPayDate(todayIso()); setPayTime(nowHM());
      setLogs(null);
      api(`/payments?job_id=${jobId}`).then(setLogs).catch(() => setLogs([]));
    }
  }, [open, balance, jobId]);
  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try { await api("/payments", { method: "POST", body: JSON.stringify({ job_id: jobId, amount: amt, method, paid_at: `${payDate}T${payTime || "00:00"}:00` }) }); toast.success("Payment recorded"); onClose(); onDone(); }
    catch (e) { toast.error(e.body?.detail?.message || e.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent><DialogHeader><DialogTitle className="font-display uppercase">Record Payment</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">AMOUNT (£)</span>
            <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payment-amount" className="w-full rounded-lg border border-input px-3.5 py-3 text-lg font-bold outline-none focus:border-brand" /></label>
          <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">METHOD</span>
            <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="payment-method" className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand">
              <option value="cash">Cash</option><option value="card">Card</option><option value="bank_transfer">Bank Transfer</option><option value="cheque">Cheque</option></select></label>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">PAYMENT DATE</span>
              <UkDatePicker value={payDate} onChange={setPayDate} testId="payment-date" /></div>
            <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">TIME</span>
              <input type="time" value={payTime} onChange={(e) => setPayTime(e.target.value)} data-testid="payment-time" className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand" /></label>
          </div>
          <PaymentLog logs={logs} />
        </div>
        <DialogFooter><button onClick={submit} disabled={saving} data-testid="payment-submit" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3 text-sm font-extrabold text-white disabled:opacity-60">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "CONFIRM PAYMENT"}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApprovalDialog({ open, onClose, jobId, onDone }) {
  const [title, setTitle] = useState("");
  const [reason, setReason] = useState("");
  const [items, setItems] = useState([{ description: "", qty: 1, unit_price: "", kind: "labour" }]);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const res = await api("/approvals", { method: "POST", body: JSON.stringify({ job_id: jobId, title: title.trim(), reason, items: items.filter((i) => i.description.trim()).map((i) => ({ ...i, qty: Number(i.qty) || 0, unit_price: Number(i.unit_price) || 0 })) }) });
      toast.success(res.email_sent ? "Approval sent to customer" : "Approval created (no customer email on file)");
      onClose(); onDone();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const upd = (i, k, v) => { const c = [...items]; c[i] = { ...c[i], [k]: v }; setItems(c); };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent><DialogHeader><DialogTitle className="font-display uppercase">Request Customer Approval</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="approval-title" placeholder="e.g. Extra work: rear brakes" className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="approval-reason" rows={2} placeholder="Why is this needed?" className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
          {items.map((it, i) => (
            <div key={i} className="flex gap-2">
              <input value={it.description} onChange={(e) => upd(i, "description", e.target.value)} placeholder="Item" className="min-w-0 flex-1 rounded-md border border-input px-2.5 py-2 text-sm outline-none focus:border-brand" />
              <input type="number" value={it.qty} onChange={(e) => upd(i, "qty", e.target.value)} className="w-14 rounded-md border border-input px-2 py-2 text-center text-sm outline-none focus:border-brand" />
              <input type="number" value={it.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} className="w-20 rounded-md border border-input px-2 py-2 text-right text-sm outline-none focus:border-brand" />
            </div>
          ))}
          <button onClick={() => setItems([...items, { description: "", qty: 1, unit_price: "", kind: "labour" }])} className="text-sm font-bold text-brand-2">+ Add item</button>
        </div>
        <DialogFooter><button onClick={submit} disabled={saving} data-testid="approval-submit" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3 text-sm font-extrabold text-white disabled:opacity-60">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "SEND TO CUSTOMER"}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
